import {
  Button,
  Center,
  chakra,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useAuth as useClerkAuth } from "@clerk/clerk-react";
import { AnimatePresence } from "framer-motion";
import { createContext, ReactNode, useContext, useState } from "react";
import { useTimeout } from "usehooks-ts";

import { useBootstrapData } from "@/auth/data/useBootstrapData";
import WarningTriangleFillSvg from "@/common/assets/warning-triangle-fill.svg?react";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import MotionBox from "@/common/components/MotionBox";
import { useSlugParam } from "@/common/hooks/useParam";
import { ENVIRONMENT } from "@/common/utils/env";
import navigateToLogin from "@/common/utils/navigateToLogin";
import {
  BootstrapAppDTO,
  BootstrapDTO,
  BootstrapOrgDTO,
} from "@reflag/shared/bootstrapAPI";
import { EnvironmentDTO } from "@reflag/shared/environmentAPI";
import { MemberRole, MemberRoles } from "@reflag/shared/permissions";
import { ReflagUser } from "@reflag/shared/types/ReflagUser";
import { OptionalKeys } from "@reflag/shared/utils/types";

const WarningTriangleFill = chakra(WarningTriangleFillSvg);

export const DEFAULT_ORG_STORAGE_KEY = "reflag-default-org-v2";

const DEFAULT_APP_STORAGE_KEY = "reflag-default-app-v2";
const DEFAULT_ENV_STORAGE_KEY = "reflag-default-env-v2";
const DEFAULT_OVERRIDDEN_ROLE_STORAGE_KEY = "reflag-default-overridden-role-v2";
const appIdStorageKey = (orgId: string) =>
  `${DEFAULT_APP_STORAGE_KEY}.${orgId}`;
const envIdStorageKey = (orgId: string) =>
  `${DEFAULT_ENV_STORAGE_KEY}.${orgId}`;
const overriddenRoleStorageKey = (orgId: string) =>
  `${DEFAULT_OVERRIDDEN_ROLE_STORAGE_KEY}.${orgId}`;

const resolveAppEnv = (
  org: BootstrapOrgDTO,
  appId?: string,
  envId?: string,
): [BootstrapAppDTO, EnvironmentDTO] | undefined => {
  const resolveByEnvId = (
    envId?: string,
  ): [BootstrapAppDTO, EnvironmentDTO] | undefined => {
    if (!org || !envId) return undefined;
    let env: EnvironmentDTO | undefined;
    const app = org.apps.find(
      ({ environments }) => (env = environments.find(({ id }) => id === envId)),
    );
    return app && env ? [app, env] : undefined;
  };

  const resolveByAppId = (
    appId?: string,
  ): [BootstrapAppDTO, EnvironmentDTO] | undefined => {
    if (!org || !appId) return undefined;
    const app = org.apps.find(({ id }) => id === appId);
    const env = app?.environments.find(({ isProduction }) => isProduction);
    return app && env ? [app, env] : undefined;
  };

  // Sort org.apps so that non-demo apps come first
  const sortedApps = [...org.apps].sort((a, b) => {
    return (a.demo ? 1 : 0) - (b.demo ? 1 : 0);
  });

  const app = sortedApps[0];
  const env = app?.environments.find(({ isProduction }) => isProduction);

  return (
    resolveByEnvId(envId) ||
    resolveByAppId(appId) ||
    (app && env ? [app, env] : undefined)
  );
};

type EnvironmentState = {
  userId?: string;
  user?: ReflagUser;
  orgId?: string;
  org?: BootstrapOrgDTO;
  appId?: string;
  app?: BootstrapAppDTO;
  envId?: string;
  env?: EnvironmentDTO;
  overriddenRole?: MemberRole;
  bootstrapFlags?: BootstrapDTO["flags"];
  intercomUserJwt?: string | null;

  clearStoredAppEnv?: () => void;
  setActiveOrg: (orgId: string, options?: { refresh: boolean }) => void;
  setOverriddenRole?: (role: MemberRole | null) => void;
  userHasRole?: (role: MemberRole) => boolean;
};

const EnvironmentContext = createContext<EnvironmentState | null>(null);

type EnvironmentProviderProps = {
  envId?: string;
  children: ReactNode;
};

const useAuth =
  ENVIRONMENT !== "test"
    ? useClerkAuth
    : () => ({ isLoaded: true, isSignedIn: true, signOut: async () => {} });

export function EnvironmentProvider({
  envId: envIdProp,
  children,
}: EnvironmentProviderProps) {
  const [showWarning, setShowWarning] = useState(false);
  const parentState = useContext<EnvironmentState | null>(EnvironmentContext);
  const { envId: envIdParam } = useSlugParam("env");
  const { isLoaded: isAuthLoaded, isSignedIn, signOut } = useAuth();

  const warningColor = useColorModeValue("red.500", "red.300");
  useTimeout(() => setShowWarning(true), 5000);

  // Load the orgId, appId, and envId from local storage
  const orgIdStored =
    localStorage.getItem(DEFAULT_ORG_STORAGE_KEY) ?? undefined;
  const appIdStored = orgIdStored
    ? (localStorage.getItem(appIdStorageKey(orgIdStored)) ?? undefined)
    : undefined;
  const envIdStored = orgIdStored
    ? (localStorage.getItem(envIdStorageKey(orgIdStored)) ?? undefined)
    : undefined;

  // Resolve the envId to use based on the order of precedence
  const envId = envIdProp || envIdParam || envIdStored;

  // Fetch the bootstrap data
  const {
    data,
    isPending,
    isLoading: isLoadingBootstrap,
    isError: isErrorBootstrap,
  } = useBootstrapData(
    orgIdStored,
    envId,
    // Don't bootstrap if already initialized or if not signed in and accessToken is missing
    !parentState && isSignedIn,
  );

  // Navigate to login if not signed in and accessToken is missing
  if (isAuthLoaded && !isSignedIn) {
    void signOut().then(() => {
      navigateToLogin();
    });
    return null;
  }

  if (!isAuthLoaded || isPending || isLoadingBootstrap || isErrorBootstrap) {
    return (
      <Center h="100vh">
        <VStack spacing={5}>
          <MotionBox layout>
            {showWarning && isErrorBootstrap ? (
              <WarningTriangleFill boxSize={5} color={warningColor} />
            ) : (
              <DelayedSpinner />
            )}
          </MotionBox>

          <AnimatePresence>
            {showWarning && (
              <MotionBox
                alignItems="center"
                animate={{ translateY: 0, opacity: 1 }}
                display="flex"
                flexDirection="column"
                gap={5}
                initial={{ translateY: 10, opacity: 0 }}
                transition={{ type: "spring", delay: 0.15 }}
              >
                {isErrorBootstrap ? (
                  <>
                    <Text textAlign="center">
                      Unable to load Reflag.
                      <br />
                      Please try reloading the page.
                    </Text>
                    <Button
                      variant="outline"
                      onClick={() => window.location.reload()}
                    >
                      Reload
                    </Button>
                  </>
                ) : (
                  <Text textAlign="center">
                    It&apos;s taking a moment to load Reflag.
                    <br />
                    Please wait a little longer.
                  </Text>
                )}
              </MotionBox>
            )}
          </AnimatePresence>
        </VStack>
      </Center>
    );
  }

  const state: EnvironmentState = {
    ...parentState,
    bootstrapFlags: data.flags,
    intercomUserJwt: data.intercomUserJwt,
    setActiveOrg: (
      orgId: string,
      options: { refresh: boolean } = { refresh: true },
    ) => {
      localStorage.setItem(DEFAULT_ORG_STORAGE_KEY, orgId);

      if (options.refresh) {
        window.location.assign("/");
      }
    },
  };

  if (data.user) {
    state.userId = data.user.id;
    state.user = data.user;
  }

  // If the org is found, set the state
  if (data.org) {
    const org = data.org;
    state.orgId = org.id;
    state.org = org;

    state.clearStoredAppEnv = () => {
      localStorage.removeItem(appIdStorageKey(org.id));
      localStorage.removeItem(envIdStorageKey(org.id));
    };

    state.setOverriddenRole = (role: MemberRole | null) => {
      if (role) {
        localStorage.setItem(overriddenRoleStorageKey(org.id), role);
      } else {
        localStorage.removeItem(overriddenRoleStorageKey(org.id));
      }

      window.location.reload();
    };

    state.userHasRole = (role: MemberRole) => {
      const actualRole = state.overriddenRole ?? state.org?.userRole;
      return (
        !!actualRole &&
        MemberRoles.indexOf(actualRole) >= MemberRoles.indexOf(role)
      );
    };

    // Store the orgId in local storage if we are at the top most level
    if (!parentState) {
      localStorage.setItem(DEFAULT_ORG_STORAGE_KEY, org.id);
    }

    // If the org has apps, resolve the app and env
    if (org.apps.length) {
      const [app, env] = resolveAppEnv(org, appIdStored, envId) ?? [];

      if (app && env) {
        const storedOverriddenRole = localStorage.getItem(
          overriddenRoleStorageKey(org.id),
        ) as MemberRole | null;

        state.appId = app.id;
        state.app = app;
        state.envId = env.id;
        state.env = env;
        state.overriddenRole = storedOverriddenRole ?? undefined;

        // Store the appId and envId in local storage if we are at the top most level
        if (!parentState) {
          localStorage.setItem(appIdStorageKey(org.id), app.id);
          localStorage.setItem(envIdStorageKey(org.id), env.id);
        }
      }
    }
  }

  return (
    <EnvironmentContext.Provider key={envId} value={state}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  const state = useContext<EnvironmentState | null>(EnvironmentContext);

  if (!state || !state.org || !state.app || !state.env) {
    throw new Error(
      "useEnvironment must be used within an EnvironmentProvider and a valid user, org, app, and env exists.",
    );
  }

  return state as OptionalKeys<Required<EnvironmentState>, "user">;
}

export function useEnvironmentUnsafe() {
  return useContext<EnvironmentState | null>(EnvironmentContext);
}
